import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  linkWithCredential,
  updateProfile,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendEmailVerification,
  applyActionCode,
  checkActionCode,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { pageUser } from '../utils/accountSwitch';
import type { AuthCredential, User } from 'firebase/auth';
import { emailVerifiedForGate } from './emailVerifiedGate';
import {
  getAuthErrorMessage,
  getEmailVerificationErrorMessage,
  ssoHandshakeError,
} from './authErrorMessages';
import { IS_PROD } from '../config/env';
import { apiService } from './api.service';

/** An email check that could not be RESOLVED, as distinct from one that
 *  resolved to "no such account". Never collapse the two: the login page sends
 *  a non-existent email to signup, so an outage treated as non-existence tells
 *  a real customer their account was deleted (2026-09-16). */
export class EmailCheckUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EmailCheckUnavailableError';
  }
}
import { safeAnalytics } from '../utils/analytics';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import {
  readPendingInvitationToken,
  clearPendingInvitationToken,
  recordSsoInvitationOutcome,
} from '../utils/pendingInvitation';
import { setRememberMe, clearRememberMe, clearAllRememberMe } from '../utils/rememberMe';
import { notifySessionRestored } from './sessionRestored';

/**
 * Authentication service
 * Handles Firebase Auth operations
 */

export interface AuthUser {
  uid: string;
  /** Internal Prisma UUID — canonical identity for all analytics. */
  internalId?: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
  photoURL: string | null;
  onboarded?: boolean;
  /**
   * True when the verified Firebase JWT carries an `internalAdmin: true`
   * custom claim. Source of truth for Bower-internal admin access. Set
   * per-user via `pnpm admin:claims grant <email>`. Propagated through
   * the login / `me` responses so the frontend can mirror the backend
   * gate without an extra round-trip.
   */
  internalAdmin?: boolean;
  lastLoggedInWorkspace?: string | null;
  /** User's chosen display name from the Bower database (set during onboarding) */
  bowerName?: string | null;
}

export interface DatabaseEmailCheckResult {
  exists: boolean;
  email: string;
  /** Firebase provider ids (e.g. 'password', 'google.com', 'microsoft.com'). */
  providers?: string[];
}

export interface EmailCheckResult {
  exists: boolean;
  hasPassword: boolean;
  providers: string[];
  /** True when the account has a Google or Microsoft SSO provider linked. */
  isSsoLinked: boolean;
}

export type SsoProvider = 'google' | 'microsoft';

/**
 * Thrown by `signInWithProvider` when Firebase refuses to auto-link a social
 * credential to an existing password account (`account-exists-with-different-
 * credential`). Carries the pending credential so the UI can run the guided
 * password-then-link recovery.
 */
export interface SsoLinkRequiredError extends Error {
  code: 'auth/account-exists-with-different-credential';
  pendingCredential: AuthCredential | null;
  email: string | null;
  /**
   * Federated providers Firebase reports as VERIFIED on the existing account
   * (from the error's `_tokenResponse.verifiedProvider`). Drives the recovery
   * UI: when it contains 'google.com', the primary path is "continue with
   * Google, then link" — a Google-only account has no password to enter, so
   * the password prompt alone is a dead-end (prod incident 2026-07-18).
   */
  verifiedProviders: string[];
}

/**
 * Build a configured Firebase auth provider for popup flows. Shared by the
 * sign-in path here and the sign-in-methods management service
 * (`signInMethods.service.ts`) so both use identical provider parameters.
 */
export function buildSsoAuthProvider(provider: SsoProvider) {
  if (provider === 'google') {
    const p = new GoogleAuthProvider();
    p.setCustomParameters({ prompt: 'select_account' });
    return p;
  }
  const p = new OAuthProvider('microsoft.com');
  // tenant 'common' = personal + work/school accounts.
  p.setCustomParameters({ prompt: 'select_account', tenant: 'common' });
  return p;
}

export interface InvitationValidationResult {
  isValid: boolean;
  reason?: string;
  invitation?: {
    id: string;
    email?: string;
    invitationType: 'APP_ACCESS' | 'WORKSPACE_JOIN';
    workspaceId?: string;
    expiresAt: string;
    createdAt: string;
  };
  /**
   * Display-friendly context joined in by the backend so the Login
   * landing page can render "Davie invited you to SMITHY" without a
   * second round-trip. Null when the invitation is non-workspace or
   * the join failed for any reason — the form still works without it.
   */
  context?: {
    workspaceName: string | null;
    workspaceSlug: string | null;
    inviterName: string | null;
    invitedEmail: string | null;
    intendedRole: string;
  } | null;
}

/**
 * Lift the AuthUser fields that only the backend knows out of a /auth/login
 * response.
 *
 * `onboarded` is the load-bearing one. `signIn()` passes the returned object
 * straight to `setUser`, and ProtectedRoute routes to /onboarding on
 * `onboarded === false` — so omitting it left `undefined`, the guard's strict
 * check missed, and `AuthRedirect` sent a half-onboarded user to /dashboard,
 * where they stayed until a full reload re-ran `onAuthStateChanged`. For a
 * brand-new user with no workspace that dashboard is the "No Workspace
 * Selected" dead end.
 *
 * `undefined` is PRESERVED when the backend omits the field — "unknown"
 * deliberately does not redirect, so coercing a missing value to `false` would
 * bounce a fully onboarded user back into the wizard.
 */
export function backendUserFields(
  responseUser: Record<string, unknown>,
  internalId: string | undefined,
): Pick<
  AuthUser,
  'internalId' | 'onboarded' | 'internalAdmin' | 'lastLoggedInWorkspace' | 'bowerName'
> {
  return {
    internalId,
    onboarded: typeof responseUser.onboarded === 'boolean' ? responseUser.onboarded : undefined,
    internalAdmin: responseUser.internalAdmin === true,
    lastLoggedInWorkspace:
      typeof responseUser.lastLoggedInWorkspace === 'string'
        ? responseUser.lastLoggedInWorkspace
        : null,
    bowerName: typeof responseUser.name === 'string' ? responseUser.name : null,
  };
}

class AuthService {
  /**
   * Validate invitation token
   */
  async validateInvitation(token: string): Promise<InvitationValidationResult> {
    try {
      const response = await apiService.post<InvitationValidationResult>('/invitations/validate', {
        token,
      });

      if (response.success && response.data) {
        return response.data;
      }

      return {
        isValid: false,
        reason: 'Invalid invitation token',
      };
    } catch (error: any) {
      console.error('🎫 AuthService: Invitation validation failed:', error);
      return {
        isValid: false,
        reason: error.message || 'Failed to validate invitation',
      };
    }
  }

  /**
   * Accept invitation token
   */
  async acceptInvitation(token: string): Promise<{
    success: boolean;
    workspaceId?: string;
    message?: string;
    error?: string;
    /** Inner gate code from the refusal envelope's `details.cause` (e.g.
     *  SEAT_LIMIT_ACCEPT_CAUSE). Null when the server named no cause or the
     *  request itself failed. Callers branch retryable-vs-dead on this. */
    errorCause?: string | null;
  }> {
    try {
      const response = await apiService.post<{ workspaceId?: string; message?: string }>(
        '/invitations/accept',
        { token },
      );

      const errorDetails =
        typeof response.error === 'object' && response.error !== null
          ? (response.error as { details?: { cause?: string } }).details
          : undefined;
      return {
        success: response.success,
        workspaceId: response.data?.workspaceId,
        message: response.data?.message,
        error: typeof response.error === 'string' ? response.error : response.error?.message,
        errorCause: errorDetails?.cause ?? null,
      };
    } catch (error: any) {
      console.error('🎫 AuthService: Invitation acceptance failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to accept invitation',
        errorCause: null,
      };
    }
  }

  /**
   * Mark user as onboarded
   */
  async markOnboarded(userWasInvited: boolean): Promise<void> {
    try {
      await apiService.post('/auth/mark-onboarded', { userWasInvited });
    } catch (error: unknown) {
      console.error('Failed to mark user as onboarded:', error);
      throw new Error(
        error instanceof Error ? error.message : 'Failed to update onboarding status',
      );
    }
  }

  /**
   * Check if email exists in our PostgreSQL database
   * This is more reliable than Firebase enumeration protection
   */
  async checkEmailInDatabase(email: string): Promise<DatabaseEmailCheckResult> {
    try {
      const response = await apiService.post<DatabaseEmailCheckResult>('/auth/check-email', {
        email,
      });

      if (response.success && response.data) {
        return response.data;
      }

      // NEVER default to exists=false. `apiService` RESOLVES with
      // `{ success: false }` on transport failure rather than throwing, so
      // this is the branch a real outage takes, and the caller routes a
      // non-existent email to the SIGNUP step. Throwing keeps the user on the
      // email step behind a retry message (rule 12, fail loud).
      throw new EmailCheckUnavailableError(response.error?.message ?? 'Check unavailable');
    } catch (error: any) {
      if (error instanceof EmailCheckUnavailableError) throw error;
      console.error('❌ Database email check failed:', error?.message, error?.response?.status);
      throw new EmailCheckUnavailableError(error?.message ?? 'Email verification failed');
    }
  }

  /**
   * Sign in with email and password.
   *
   * Eagerly resolves the internal Prisma UUID so the caller can fire
   * `USER_SIGNED_IN` (and anything downstream) under the canonical PostHog
   * `distinct_id`. Without this, the event is tracked under the Firebase UID
   * and `onAuthStateChanged` has to retroactively alias it. The extra
   * `/auth/login` round-trip is the same call `onAuthStateChanged` will make
   * a few ms later anyway, so this is at worst a latency reordering.
   */
  async signInWithPassword(
    email: string,
    password: string,
    rememberMe: boolean = false,
  ): Promise<AuthUser> {
    try {
      // Sweep rememberMe entries a previous user left on this device, which
      // would otherwise linger until their 30-day expiry.
      clearAllRememberMe();

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const formatted = {
        ...this.formatUser(userCredential.user),
        emailVerified: await emailVerifiedForGate(userCredential.user),
      };

      // Best-effort enrichment — never block sign-in on /auth/login. If the
      // backend is slow or unreachable, `onAuthStateChanged` will retry and
      // alias the historical events once identity is available.
      try {
        const token = await userCredential.user.getIdToken();
        const response = await apiService.post('/auth/login', {
          idToken: token,
          manualLogin: true,
          rememberMe,
        });
        if (
          response.success &&
          response.data &&
          typeof response.data === 'object' &&
          'user' in response.data
        ) {
          const responseData = response.data as Record<string, unknown>;
          const responseUser =
            responseData.user && typeof responseData.user === 'object' && 'id' in responseData.user
              ? (responseData.user as Record<string, unknown>)
              : null;
          const internalId =
            responseUser && typeof responseUser.id === 'string' ? responseUser.id : undefined;
          if (responseUser && internalId) {
            if (rememberMe) {
              setRememberMe(internalId);
            } else {
              clearRememberMe(internalId);
            }
            return { ...formatted, ...backendUserFields(responseUser, internalId) };
          }
        }
      } catch (enrichError) {
        console.warn('Could not enrich signed-in user with internalId:', enrichError);
      }

      // Fallback: apply rememberMe using the Firebase UID when internalId is unavailable
      if (rememberMe) {
        setRememberMe(formatted.uid);
      } else {
        clearRememberMe(formatted.uid);
      }

      return formatted;
    } catch (error: any) {
      console.error('Password sign in failed:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string): Promise<void> {
    try {
      // Configure custom action URL to point to our universal auth action handler
      // Use production domain for consistency
      const baseUrl = IS_PROD ? 'https://app.bowerlabs.ai' : window.location.origin;

      const actionCodeSettings = {
        url: `${baseUrl}/auth/action`,
        handleCodeInApp: true,
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);
    } catch (error: any) {
      console.error('Password reset email failed:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Verify password reset code
   */
  async verifyPasswordResetCode(code: string): Promise<string> {
    try {
      return await verifyPasswordResetCode(auth, code);
    } catch (error: any) {
      console.error('Password reset code verification failed:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Complete password reset with new password
   */
  async completePasswordReset(code: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(auth, code, newPassword);
    } catch (error: any) {
      console.error('Password reset completion failed:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Apply action code (for email verification, etc.)
   */
  async applyActionCode(code: string): Promise<void> {
    try {
      await applyActionCode(auth, code);
    } catch (error: any) {
      console.error('Action code application failed:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Verify email using action code (with email verification-specific error messages)
   */
  async verifyEmail(code: string): Promise<void> {
    try {
      await applyActionCode(auth, code);
    } catch (error: any) {
      console.error('Email verification failed:', error);
      throw new Error(this.getEmailVerificationErrorMessage(error.code));
    }
  }

  /**
   * Recover (revert) an email change via a Firebase `recoverEmail` action code.
   * Inspects the code first so the UI can confirm which address was restored,
   * then applies it. An unexpected email change can signal a compromised
   * account, so callers should offer a password reset on the restored address
   * (Firebase's recommended recovery flow).
   */
  async recoverEmail(code: string): Promise<string | null> {
    try {
      const info = await checkActionCode(auth, code);
      await applyActionCode(auth, code);
      return info.data.email ?? null;
    } catch (error: any) {
      // Log only the Firebase error code — the full error object can carry the
      // user's email in `customData` (PII; compliance.md bans PII in logs).
      console.error('Email recovery failed:', error?.code ?? 'unknown');
      throw new Error(this.getErrorMessage(error?.code));
    }
  }

  /**
   * Get password reset parameters from URL
   */
  getPasswordResetParamsFromUrl(): { mode: string | null; oobCode: string | null } {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      mode: urlParams.get('mode'),
      oobCode: urlParams.get('oobCode'),
    };
  }

  /**
   * Clear password reset parameters from URL without page reload
   */
  clearPasswordResetParamsFromUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('mode');
    url.searchParams.delete('oobCode');
    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Change password (requires current password)
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = pageUser(auth.currentUser);
    if (!user || !user.email) {
      throw new Error('No user is currently signed in');
    }

    try {
      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);
    } catch (error: any) {
      console.error('Password change failed:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Send email verification to current user
   */
  async sendEmailVerification(): Promise<void> {
    const user = pageUser(auth.currentUser);
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    const emailDomain = user.email?.split('@')[1] ?? null;
    safeAnalytics.track(ANALYTICS_EVENTS.VERIFICATION_EMAIL_SEND_REQUESTED, {
      email_domain: emailDomain,
    });

    // Mirror sendPasswordReset: route the action link through our universal
    // /auth/action handler so the link is environment-correct (and the email
    // continues to work when the user opens it on a different device or in a
    // mobile WebView). Without this, the link relies on the project-level
    // callbackUri which always points at production.
    const baseUrl = IS_PROD ? 'https://app.bowerlabs.ai' : window.location.origin;
    const actionCodeSettings = {
      url: `${baseUrl}/auth/action`,
      handleCodeInApp: true,
    };

    try {
      await sendEmailVerification(user, actionCodeSettings);
      safeAnalytics.track(ANALYTICS_EVENTS.VERIFICATION_EMAIL_SEND_SUCCEEDED, {
        email_domain: emailDomain,
      });
    } catch (error) {
      const code =
        error && typeof error === 'object' && 'code' in error
          ? ((error as { code?: string }).code ?? 'unknown')
          : 'unknown';
      safeAnalytics.track(ANALYTICS_EVENTS.VERIFICATION_EMAIL_SEND_FAILED, {
        email_domain: emailDomain,
        error_code: code,
      });
      throw new Error(this.getErrorMessage(code));
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(updates: { displayName?: string; photoURL?: string }): Promise<AuthUser> {
    const user = pageUser(auth.currentUser);
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      await updateProfile(user, updates);
      // Claim-aware: callers setUser() with this return and no auth-state tick
      // corrects it, so the raw bit would bounce a claim-holder to the wall.
      return { ...this.formatUser(user), emailVerified: await emailVerifiedForGate(user) };
    } catch (error: any) {
      console.error('Profile update failed:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * Check email methods (simplified - returns database check result)
   */
  async checkEmailMethods(email: string): Promise<EmailCheckResult> {
    try {
      const dbCheck = await this.checkEmailInDatabase(email);
      // Trust the backend's Firebase provider list when present; fall back to
      // the legacy "exists ⇒ password" assumption for older backends.
      const providers = dbCheck.providers ?? (dbCheck.exists ? ['password'] : []);
      return {
        exists: dbCheck.exists,
        hasPassword: providers.includes('password'),
        providers,
        isSsoLinked: providers.includes('google.com') || providers.includes('microsoft.com'),
      };
    } catch (error: any) {
      // Must propagate: swallowing it back into `exists: false` reopens the
      // same door via the SSO path.
      if (error instanceof EmailCheckUnavailableError) throw error;
      console.error('Email methods check failed:', error);
      return {
        exists: false,
        hasPassword: false,
        providers: [],
        isSsoLinked: false,
      };
    }
  }

  /**
   * Create account with password (alias for signUp for compatibility)
   */
  async createAccountWithPassword(
    email: string,
    password: string,
    name?: string,
  ): Promise<AuthUser> {
    return this.signUp(email, password, name);
  }

  /**
   * Sign up - Create new account (email + password only)
   * Backend creates both Firebase user + PostgreSQL user atomically
   * Then sign in with Firebase to get the user token
   */
  async signUp(
    email: string,
    password: string,
    name?: string,
    turnstileToken?: string,
    signupExtras?: {
      signupSignals?: import('@bower/shared').SignupSignals;
      honeypot?: string;
    },
  ): Promise<AuthUser> {
    try {
      const invitationToken = readPendingInvitationToken();

      // Call backend signup API - it creates both Firebase and PostgreSQL users
      const signupResponse = await apiService.post('/auth/signup', {
        email: email,
        password: password,
        name: name,
        invitationToken: invitationToken || undefined,
        turnstileToken: turnstileToken || undefined,
        signupSignals: signupExtras?.signupSignals,
        honeypot: signupExtras?.honeypot,
      });

      // If invitation was processed successfully, clear the carrier from
      // both URL and localStorage so subsequent loads don't re-attempt.
      if (
        invitationToken &&
        signupResponse.success &&
        signupResponse.data &&
        typeof signupResponse.data === 'object' &&
        'invitationAccepted' in signupResponse.data &&
        (signupResponse.data as { invitationAccepted?: boolean }).invitationAccepted
      ) {
        clearPendingInvitationToken();
      }

      if (!signupResponse.success) {
        throw new Error(signupResponse.error?.message || 'Failed to create account');
      }

      // Capture the internal Prisma UUID from the signup response so the
      // caller can set the correct PostHog distinct_id on the local SDK,
      // matching the distinct_id used by the server-side User Signed Up
      // capture in auth.controller.ts. Without this, post-signup client
      // events would land under the Firebase UID and onAuthStateChanged
      // would have to retroactively alias them.
      const internalId =
        signupResponse.data &&
        typeof signupResponse.data === 'object' &&
        'user' in signupResponse.data
          ? ((signupResponse.data as any).user?.id as string | undefined)
          : undefined;

      // Now sign in with Firebase to get the auth token
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const formatted = this.formatUser(userCredential.user);
      return { ...formatted, internalId: internalId ?? formatted.internalId };
    } catch (error: any) {
      console.error('Sign up failed:', error);
      throw new Error(error.message || 'Authentication failed. Please try again');
    }
  }

  /**
   * Sign in (or sign up) with a social provider via Firebase popup, then sync
   * to our backend (`/auth/sso-signin`) so a Postgres user exists. Returns the
   * AuthUser carrying the internal Prisma UUID for analytics.
   *
   * On `auth/account-exists-with-different-credential` (a password account
   * Firebase won't auto-link — the Microsoft-into-password case) we throw an
   * `SsoLinkRequiredError` carrying the pending credential so the UI can run
   * the guided password-then-link recovery via `linkPendingCredential`.
   */
  async signInWithProvider(provider: SsoProvider): Promise<AuthUser> {
    // Sweep stale rememberMe entries from a previous user on this device,
    // mirroring signInWithPassword.
    clearAllRememberMe();

    const authProvider = this.buildAuthProvider(provider);
    let credential;
    try {
      credential = await signInWithPopup(auth, authProvider);
    } catch (error: any) {
      if (error?.code === 'auth/account-exists-with-different-credential') {
        const pending =
          provider === 'google'
            ? GoogleAuthProvider.credentialFromError(error)
            : OAuthProvider.credentialFromError(error);
        const linkError = new Error(this.getErrorMessage(error.code)) as SsoLinkRequiredError;
        linkError.code = 'auth/account-exists-with-different-credential';
        linkError.pendingCredential = pending;
        linkError.email = error?.customData?.email ?? null;
        // Firebase's REST response lists the existing account's VERIFIED
        // federated providers (e.g. ['google.com']). Undocumented-but-stable
        // location; default to [] so recovery degrades to the password path.
        linkError.verifiedProviders =
          (error?.customData?._tokenResponse as { verifiedProvider?: string[] } | undefined)
            ?.verifiedProvider ?? [];
        throw linkError;
      }
      console.error('SSO sign-in failed:', error);
      throw new Error(this.getErrorMessage(error?.code));
    }

    return this.syncSsoUser(credential.user);
  }

  /**
   * Collision recovery for accounts with NO password (e.g. Google-only): sign
   * in with the account's verified provider, then link the held pending
   * credential so the collided provider works directly next time.
   *
   * Returns whether the link landed — a link failure must NOT fail the
   * sign-in (the user is authenticated either way; they can connect the
   * provider later in Settings → Security), but it must not be silent either
   * (rule 12): the caller adapts its success message on `linked: false`.
   */
  async signInWithProviderThenLink(
    provider: SsoProvider,
    pending: AuthCredential,
  ): Promise<{ authUser: AuthUser; linked: boolean }> {
    clearAllRememberMe();
    let credential;
    try {
      credential = await signInWithPopup(auth, this.buildAuthProvider(provider));
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error?.code));
    }
    let linked = false;
    try {
      await linkWithCredential(credential.user, pending);
      linked = true;
    } catch (linkErr) {
      // Pending OAuth credentials are single-use and short-lived — an expired
      // one is the common failure here. Sign-in still stands.
      console.warn('Could not link the pending credential after sign-in:', linkErr);
    }
    const authUser = await this.syncSsoUser(credential.user, true);
    return { authUser, linked };
  }

  /**
   * Complete a pending social link after the user proves password ownership.
   * Signs in with the existing password, links the held social credential, then
   * syncs to the backend.
   */
  async linkPendingCredential(
    email: string,
    password: string,
    pending: AuthCredential,
  ): Promise<AuthUser> {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await linkWithCredential(cred.user, pending);
    return this.syncSsoUser(cred.user, true);
  }

  /**
   * Record that Firebase presented a credential-collision recovery prompt.
   * This is server-side because pre-auth browser analytics is consent-gated;
   * it carries only the provider and never blocks recovery.
   */
  async recordSsoCollisionRecoveryShown(provider: SsoProvider): Promise<void> {
    try {
      await apiService.post('/auth/sso-collision-recovery', { provider });
    } catch {
      // Observability must not alter the already-recovered auth flow.
    }
  }

  /** Build a configured Firebase auth provider for the popup flow. */
  private buildAuthProvider(provider: SsoProvider) {
    return buildSsoAuthProvider(provider);
  }

  /**
   * POST the Firebase user's ID token to `/auth/sso-signin` (creates/fetches the
   * Postgres user), then RELOAD the record and FORCE-REFRESH the ID token. The
   * refresh is essential for Microsoft: the backend grants the `app_access_ok`
   * custom claim, but the popup token predates it — `requireEmailVerification`
   * reads the token, so without a refresh the user would be blocked. The reload
   * is essential for Google and Apple: it picks up a verified bit the backend
   * restored.
   */
  private async syncSsoUser(user: User, collisionRecovery = false): Promise<AuthUser> {
    const formatted = this.formatUser(user);
    const invitationToken = readPendingInvitationToken();
    // Clear the carrier BEFORE the POST, not after it: Firebase's auth listener
    // sets the user while this call is in flight, and a still-readable token
    // lets AuthRedirect accept it too. The user re-set when this call returns
    // then cancels AuthRedirect's workspace switch, and the invitee lands in
    // their old workspace. The server is the only acceptor on this path;
    // `recordSsoInvitationOutcome` below is how the app learns where it landed
    // or why it was refused.
    if (invitationToken) clearPendingInvitationToken();
    let internalId: string | undefined;
    // Hoisted out of the try so the backend fields survive to the return.
    // Narrowing this to `{ id?: string }` is what dropped `onboarded` on every
    // Google/Microsoft sign-in: the value is on the wire
    // (auth-sso.controller.ts returns it) and was discarded before anything
    // could read it, so `ProtectedRoute`'s strict `=== false` missed and a new
    // SSO user landed on the dashboard. `signInWithProvider` clears the cached
    // workspace immediately before this, so that landing is the "No Workspace
    // Selected" dead end with nothing to soften it.
    let responseUser: Record<string, unknown> | undefined;
    let refusal: string | undefined;
    try {
      const token = await user.getIdToken();
      // apiService.post already retries a failed request once.
      const response = await apiService.post('/auth/sso-signin', {
        idToken: token,
        invitationToken: invitationToken || undefined,
        ...(collisionRecovery ? { collisionRecovery: true } : {}),
      });
      if (
        response.success &&
        response.data &&
        typeof response.data === 'object' &&
        'user' in response.data
      ) {
        responseUser = (response.data as { user?: Record<string, unknown> }).user;
        internalId =
          responseUser && typeof responseUser.id === 'string' ? responseUser.id : undefined;
        recordSsoInvitationOutcome(response.data, invitationToken, user.uid);
      } else if (response.error?.status && response.error.status < 500) {
        refusal = response.error.message;
      }
    } catch (err) {
      console.warn('Could not sync SSO user with backend:', err);
    }
    // No row, no session: the app would refuse every request, with nothing to retry.
    if (!responseUser || !internalId) {
      await signOut(auth).catch((e: unknown) => console.warn('SSO sign-out failed:', e));
      throw ssoHandshakeError(refusal, Boolean(invitationToken));
    }
    // Force-refresh for the app_access_ok claim the backend just granted
    // (Microsoft); reload for a verified bit it restored. The gate below reads both.
    await user.reload().catch((e: unknown) => console.warn('SSO user reload failed:', e));
    try {
      await user.getIdToken(true);
    } catch (refreshErr) {
      console.warn('SSO token force-refresh failed:', refreshErr);
    }
    return {
      ...formatted,
      ...backendUserFields(responseUser, internalId),
      // Post-refresh, and last: it wins over anything the backend payload carries.
      emailVerified: await emailVerifiedForGate(user),
    };
  }

  /**
   * Sign out current user.
   *
   * Clears rememberMe entries under both the internal Prisma UUID and the
   * Firebase UID. During sign-in, the rememberMe entry is written under the
   * internalId when `/auth/login` enrichment succeeds, or under the Firebase
   * UID as a fallback when enrichment fails. Clearing both ids guarantees the
   * entry is removed regardless of which key was used, even when
   * `onAuthStateChanged` later enriches `internalId` into state after the
   * fallback write.
   */
  async signOut(internalId?: string, firebaseUid?: string): Promise<void> {
    if (internalId) clearRememberMe(internalId);
    if (firebaseUid && firebaseUid !== internalId) clearRememberMe(firebaseUid);
    await signOut(auth);
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Get auth token for API calls
   * Firebase automatically refreshes expired tokens when forceRefresh=false (default)
   */
  async getAuthToken(forceRefresh = false): Promise<string | null> {
    // Another account took over this profile: not this page's token to hand
    // out (utils/accountSwitch.ts).
    const user = pageUser(auth.currentUser);
    if (!user) return null;

    try {
      // Firebase SDK automatically handles token refresh when the token is expired
      // unless forceRefresh=true is passed
      return await user.getIdToken(forceRefresh);
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChanged(callback: (user: AuthUser | null) => void) {
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        callback(null);
        return;
      }

      // Claim-aware verified bit for the App.tsx gate (MS users carry
      // app_access_ok, not a record bit). Computed BEFORE /auth/login so ALL
      // emit paths use it — a transient login failure must not bounce a
      // claim-holder to the verify-email wall (cached token, no network).
      const gateVerified = await emailVerifiedForGate(user);
      // Not awaited by Firebase: a sign-out during /auth/login must stay signed out.
      const emit = (authUser: AuthUser) => {
        if (auth.currentUser?.uid === user.uid) callback(authUser);
      };

      try {
        // Get complete user data from backend including onboarded status
        const token = await user.getIdToken();
        notifySessionRestored(user.uid, token);
        const response = await apiService.post('/auth/login', { idToken: token });

        if (
          response.success &&
          response.data &&
          typeof response.data === 'object' &&
          'user' in response.data &&
          response.data.user
        ) {
          const backendUser = response.data.user as Record<string, unknown>;

          // Same five fields, same endpoint, same contract as the manual
          // sign-in path — so it goes through the same helper. Two hand-rolled
          // copies is how `onboarded` came to be lifted on one path and
          // dropped on the other, which is the bug `backendUserFields`
          // documents.
          const completeUser: AuthUser = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            emailVerified: gateVerified,
            photoURL: user.photoURL,
            ...backendUserFields(
              backendUser,
              typeof backendUser.id === 'string' ? backendUser.id : undefined,
            ),
          };

          emit(completeUser);
        } else {
          // Fallback to Firebase data only (onboarded will be undefined)
          emit({ ...this.formatUser(user), emailVerified: gateVerified });
        }
      } catch (error) {
        console.error('Failed to get complete user data:', error);
        // Fallback to Firebase data only — still claim-aware (see above).
        emit({ ...this.formatUser(user), emailVerified: gateVerified });
      }
    });
  }

  /**
   * Format Firebase user for app use
   */
  private formatUser(user: User): AuthUser {
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      emailVerified: user.emailVerified,
      photoURL: user.photoURL,
    };
  }

  private getErrorMessage(errorCode: string): string {
    return getAuthErrorMessage(errorCode);
  }

  private getEmailVerificationErrorMessage(errorCode: string): string {
    return getEmailVerificationErrorMessage(errorCode);
  }
}

export const authService = new AuthService();
